import { downloadPurchaseOrderAttachment } from '../../../api/purchaseOrder';
import {
  calcOrderLineAmount,
  formatOrderQty,
  formatWon,
  PurchaseOrder,
  purchaseOrderHasApprover,
  purchaseOrderStatusLabel,
} from '../../../types/purchaseOrder';
import ApprovalStampBox from '../ApprovalStampBox';
import { formatDate, PreviewAttachmentList } from './previewShared';
import '../../contract/ContractPanel.css';
import '../../product/InspectionFormSheet.css';
import '../../product/PurchaseOrderPanel.css';

interface Props {
  detail: PurchaseOrder;
}

function textOrDash(value?: string | null) {
  const text = value?.trim();
  return text ? text : '-';
}

function PurchaseOrderPreviewView({ detail }: Props) {
  const attachments = detail.attachments ?? [];
  const statusText = purchaseOrderStatusLabel(
    detail.status,
    detail.approvalInProgress,
    purchaseOrderHasApprover(detail)
  );

  return (
    <div className="po-detail">
      <div className="ifs-sheet" style={{ marginBottom: 12 }}>
        <div className="ifs-sheet__top">
          <div>
            <h3 style={{ margin: '0 0 4px' }}>발주서</h3>
            <p className="contract-panel__hint" style={{ margin: 0 }}>
              {textOrDash(detail.orderNo)} · {statusText}
            </p>
          </div>
          <ApprovalStampBox
            authorName={detail.authorName}
            reviewerName={detail.reviewerName}
            approverName={detail.approverName}
          />
        </div>
      </div>

      <section className="po-form__card">
        <h3>기본 정보</h3>
        <dl className="po-detail__dl">
          <div>
            <dt>계약</dt>
            <dd>
              {textOrDash(detail.contractName)}
              {detail.contractNo ? ` (${detail.contractNo})` : ''}
            </dd>
          </div>
          <div>
            <dt>발주번호</dt>
            <dd>{textOrDash(detail.orderNo)}</dd>
          </div>
          <div>
            <dt>발주일</dt>
            <dd>{formatDate(detail.orderDate)}</dd>
          </div>
          <div>
            <dt>납기일</dt>
            <dd>{formatDate(detail.deliveryDueDate)}</dd>
          </div>
          <div>
            <dt>상태</dt>
            <dd>
              <span className={`po-status is-${(detail.status || 'DRAFT').toLowerCase()}`}>
                {statusText}
              </span>
            </dd>
          </div>
        </dl>
      </section>
      <section className="po-form__card">
        <h3>업체</h3>
        <dl className="po-detail__dl">
          <div>
            <dt>업체명</dt>
            <dd>{textOrDash(detail.vendorName)}</dd>
          </div>
          <div>
            <dt>담당자</dt>
            <dd>{textOrDash(detail.vendorContactName)}</dd>
          </div>
          <div>
            <dt>연락처</dt>
            <dd>{textOrDash(detail.vendorPhone)}</dd>
          </div>
          <div>
            <dt>Fax</dt>
            <dd>{textOrDash(detail.vendorFax)}</dd>
          </div>
        </dl>
      </section>
      <section className="po-form__card">
        <h3>품목</h3>
        {!detail.items || detail.items.length === 0 ? (
          <p className="contract-panel__hint">품목이 없습니다.</p>
        ) : (
          <>
            <table className="po-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>품명</th>
                  <th>규격</th>
                  <th>수량</th>
                  <th>단위</th>
                  <th>단가</th>
                  <th>합계</th>
                  <th>비고</th>
                </tr>
              </thead>
              <tbody>
                {detail.items.map((item, index) => {
                  const amount =
                    item.amount != null && item.amount !== ''
                      ? Number(item.amount)
                      : calcOrderLineAmount(item.orderQty, item.unitPrice);
                  return (
                    <tr key={item.itemId ?? index}>
                      <td>{index + 1}</td>
                      <td>{textOrDash(item.itemName)}</td>
                      <td>{textOrDash(item.specText)}</td>
                      <td>{formatOrderQty(item.orderQty)}</td>
                      <td>{textOrDash(item.unit)}</td>
                      <td>
                        {item.unitPrice == null || item.unitPrice === ''
                          ? '-'
                          : Number(item.unitPrice).toLocaleString('ko-KR')}
                      </td>
                      <td>{amount == null ? '-' : amount.toLocaleString('ko-KR')}</td>
                      <td>{textOrDash(item.remark)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="po-lines__total is-detail">
              <span>통합 합계</span>
              <strong>
                {detail.totalAmount != null && detail.totalAmount !== ''
                  ? formatWon(detail.totalAmount)
                  : formatWon(
                      detail.items.reduce((sum, item) => {
                        const amount =
                          item.amount != null && item.amount !== ''
                            ? Number(item.amount)
                            : calcOrderLineAmount(item.orderQty, item.unitPrice);
                        return amount == null ? sum : sum + amount;
                      }, 0)
                    )}
              </strong>
            </div>
          </>
        )}
      </section>
      <section className="po-form__card">
        {detail.memo ? (
          <>
            <h3>메모</h3>
            <p style={{ whiteSpace: 'pre-wrap', marginTop: 0 }}>{detail.memo}</p>
          </>
        ) : null}
        <div style={{ marginTop: detail.memo ? 16 : 0 }}>
          <h3 style={{ marginTop: 0 }}>첨부파일</h3>
          <PreviewAttachmentList
            files={attachments}
            onDownload={(file) => {
              void downloadPurchaseOrderAttachment(detail.purchaseOrderId, file.attachmentId)
                .then((blob) => {
                  const url = window.URL.createObjectURL(blob);
                  const anchor = document.createElement('a');
                  anchor.href = url;
                  anchor.download = file.fileName || 'attachment';
                  anchor.click();
                  window.URL.revokeObjectURL(url);
                })
                .catch(() => undefined);
            }}
          />
        </div>
      </section>
    </div>
  );
}

export default PurchaseOrderPreviewView;
